import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserPlus, AlertTriangle, Dumbbell, Clock, Search, CheckCircle, X, TrendingUp, CalendarX, CalendarDays, CreditCard } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import { useApp } from '../context/AppContext';
import { formatDate, daysUntilExpiry, getTodayString, getBusinessHoursSlot, formatHoursSlot } from '../utils/helpers';
import { format, subDays } from 'date-fns';
import { ko } from 'date-fns/locale';

const SkeletonCard = () => (
  <div className="card p-5 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="space-y-2 flex-1">
        <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-8 w-14 bg-gray-200 dark:bg-gray-700 rounded mt-1" />
        <div className="h-2.5 w-10 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
      <div className="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
    </div>
  </div>
);

const SkeletonBlock = ({ height = 160 }) => (
  <div className="card p-5 animate-pulse">
    <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
    <div className="bg-gray-100 dark:bg-gray-700/50 rounded-xl" style={{ height }} />
  </div>
);

const StatCard = ({ icon: Icon, label, value, color, onClick, sub }) => (
  <div
    onClick={onClick}
    className={`card p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color.replace('text-', 'bg-').replace('-600', '-100').replace('-400', '-900/30')}`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
  </div>
);

// 빠른 출석 체크 인라인 컴포넌트
const QuickCheckin = () => {
  const { members, attendances, addAttendance, memberships } = useApp();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const today = getTodayString();

  const handleSearch = (q) => {
    setSearch(q);
    if (!q.trim()) { setResults([]); return; }
    const lower = q.toLowerCase();
    setResults(members.filter(m => m.name?.toLowerCase().includes(lower) || m.phone?.includes(q)).slice(0, 4));
  };

  const isChecked = (id) => attendances.some(a => a.memberId === id && a.date === today);

  const getWarning = (memberId) => {
    const active = memberships.filter(ms => ms.memberId === memberId && ms.status !== 'expired' && !ms.frozenAt);
    if (!active.length) return 'none';
    const min = active.reduce((m, ms) => { const d = daysUntilExpiry(ms.endDate); return (d !== null && (m === null || d < m)) ? d : m; }, null);
    if (min === null || min < 0) return 'expired';
    if (min <= 7) return 'expiring';
    return 'ok';
  };

  const handleCheckin = (member) => {
    addAttendance(member.id);
    setSearch('');
    setResults([]);
  };

  return (
    <div className="card p-5">
      <h2 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
        <CheckCircle className="w-4 h-4 text-green-500" />
        빠른 출석 체크
      </h2>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="이름 또는 전화번호"
          value={search}
          onChange={e => handleSearch(e.target.value)}
          className="input pl-9"
          autoComplete="off"
        />
        {search && (
          <button onClick={() => { setSearch(''); setResults([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {results.length > 0 && (
        <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          {results.map(m => {
            const checked = isChecked(m.id);
            const warn = getWarning(m.id);
            return (
              <div key={m.id} className={`flex items-center justify-between px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0 ${warn === 'expired' ? 'bg-red-50/50 dark:bg-red-900/10' : warn === 'expiring' ? 'bg-yellow-50/50 dark:bg-yellow-900/10' : ''}`}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-semibold text-primary-700 dark:text-primary-400">{m.name?.[0]}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{m.name}</span>
                      {warn === 'expired' && <span className="text-xs text-red-500 font-medium">만료</span>}
                      {warn === 'expiring' && <span className="text-xs text-yellow-500 font-medium">임박</span>}
                    </div>
                    <span className="text-xs text-gray-400">{m.phone}</span>
                  </div>
                </div>
                {checked
                  ? <span className="badge-green text-xs">완료</span>
                  : <button onClick={() => handleCheckin(m)} className="btn-primary text-xs px-2.5 py-1">체크인</button>
                }
              </div>
            );
          })}
        </div>
      )}
      {search && results.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-3">검색 결과 없음</p>
      )}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { members, memberships, attendances, ptAssignments, gymSettings, dataLoading } = useApp();
  const today = getTodayString();
  const now = new Date();

  const todayKorean = format(now, 'yyyy년 M월 d일 (EEE)', { locale: ko });

  // 통계 계산
  const todayAttendances = attendances.filter(a => a.date === today);
  const thisMonth = now.toISOString().slice(0, 7);
  const newMembersThisMonth = members.filter(m => m.createdAt?.startsWith(thisMonth)).length;
  const activeMembers = members.filter(m => m.status !== 'inactive' && m.status !== 'withdrawn');

  const expiringMemberships = memberships.filter(m => {
    if (m.status === 'expired' || m.frozenAt) return false;
    const days = daysUntilExpiry(m.endDate);
    return days !== null && days >= 0 && days <= 7;
  });

  const expiringThisMonth = memberships.filter(m => {
    if (m.status === 'expired' || m.frozenAt) return false;
    return m.endDate?.startsWith(thisMonth);
  });

  const activePt = ptAssignments.filter(p => p.status === 'active');
  const lowRemainPt = activePt.filter(p => (p.totalSessions - p.usedSessions) <= 3);

  // 최근 7일 출석 데이터 (주간 바차트)
  const weeklyData = Array.from({ length: 7 }, (_, i) => {
    const d = subDays(now, 6 - i);
    const dateStr = format(d, 'yyyy-MM-dd');
    const isToday = dateStr === today;
    return {
      label: format(d, 'EEE', { locale: ko }),
      count: attendances.filter(a => a.date === dateStr).length,
      isToday,
    };
  });

  // 최근 출석
  const recentAttendances = [...attendances]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 8);

  return (
    <div className="space-y-5">
      {/* 날짜 + 인사 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{todayKorean}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">
            {gymSettings?.name || 'GYM Manager'}에 오신 걸 환영합니다 👋
          </p>
        </div>
        {(() => {
          const slot = getBusinessHoursSlot(gymSettings?.businessHours);
          const text = formatHoursSlot(slot);
          if (!text) return null;
          return (
            <div className="hidden sm:flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-full">
              <Clock className="w-3.5 h-3.5" />
              {text}
            </div>
          );
        })()}
      </div>

      {/* 빠른 액션 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: UserPlus, label: '회원 등록', color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20', path: 'members' },
          { icon: Dumbbell,  label: 'PT 부여',   color: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20', path: 'pt' },
          { icon: CalendarDays, label: 'PT 일정', color: 'text-green-600 bg-green-50 dark:bg-green-900/20', path: 'pt' },
          { icon: CreditCard, label: '회원권 등록', color: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20', path: 'memberships' },
        ].map(({ icon: Icon, label, color, path }) => (
          <button
            key={label}
            onClick={() => navigate(path)}
            className="card p-4 flex flex-col items-center gap-2 hover:shadow-md active:scale-95 transition-all"
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
          </button>
        ))}
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {dataLoading ? (
          <>
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
            <SkeletonCard /><SkeletonCard /><SkeletonCard />
          </>
        ) : (
          <>
            <StatCard icon={Users} label="오늘 출석" value={todayAttendances.length} color="text-blue-600 dark:text-blue-400" sub="명" onClick={() => navigate('attendance')} />
            <StatCard icon={TrendingUp} label="전체 활성 회원" value={activeMembers.length} color="text-green-600 dark:text-green-400" sub={`이번달 신규 ${newMembersThisMonth}명`} onClick={() => navigate('members')} />
            <StatCard icon={Dumbbell} label="PT 진행중" value={activePt.length} color="text-purple-600 dark:text-purple-400" sub={lowRemainPt.length > 0 ? `잔여 3회 이하 ${lowRemainPt.length}건` : '건'} onClick={() => navigate('pt')} />
            <StatCard icon={AlertTriangle} label="만료 임박" value={expiringMemberships.length} color="text-yellow-600 dark:text-yellow-400" sub="7일 이내" onClick={() => navigate('notifications')} />
            <StatCard icon={CalendarX} label="이번달 만료 예정" value={expiringThisMonth.length} color="text-orange-600 dark:text-orange-400" sub={thisMonth.replace('-', '년 ') + '월'} onClick={() => navigate('notifications')} />
            <StatCard icon={UserPlus} label="이번달 신규 등록" value={newMembersThisMonth} color="text-teal-600 dark:text-teal-400" sub="명 가입" onClick={() => navigate('members')} />
          </>
        )}
      </div>

      {/* 주간 출석 + 빠른 출석 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* 주간 출석 바차트 */}
        {dataLoading ? <SkeletonBlock height={160} /> : <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-500" />
            최근 7일 출석 현황
          </h2>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={weeklyData} margin={{ top: 0, right: 0, left: -30, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v) => [`${v}명`, '출석']}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {weeklyData.map((d, i) => (
                  <Cell key={i} fill={d.isToday ? '#3b82f6' : '#bfdbfe'} className="dark:fill-primary-800" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-400 text-center mt-1">파란 막대 = 오늘</p>
        </div>}

        {/* 빠른 출석 체크 */}
        {dataLoading ? <SkeletonBlock height={160} /> : <QuickCheckin />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* 만료 임박 회원 */}
        {dataLoading ? <SkeletonBlock height={180} /> : <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-500" />
              만료 임박 회원
            </h2>
            <button onClick={() => navigate('/notifications')} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">전체보기</button>
          </div>
          {expiringMemberships.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">만료 임박 회원이 없습니다.</p>
          ) : (
            <div className="space-y-1">
              {expiringMemberships.slice(0, 5).map(ms => {
                const member = members.find(m => m.id === ms.memberId);
                const days = daysUntilExpiry(ms.endDate);
                if (!member) return null;
                return (
                  <div key={ms.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => navigate(`/members/${member.id}`)}>
                    <div>
                      <span className="font-medium text-sm text-gray-900 dark:text-white">{member.name}</span>
                      <span className="text-xs text-gray-400 ml-2">{ms.productName}</span>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${days <= 1 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : days <= 3 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                      D-{days}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>}

        {/* 최근 출석 현황 */}
        {dataLoading ? <SkeletonBlock height={180} /> : <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              최근 출석 현황
            </h2>
            <button onClick={() => navigate('/attendance')} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">전체보기</button>
          </div>
          {recentAttendances.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">출석 기록이 없습니다.</p>
          ) : (
            <div className="space-y-1">
              {recentAttendances.map(att => {
                const member = members.find(m => m.id === att.memberId);
                if (!member) return null;
                const isToday = att.date === today;
                return (
                  <div key={att.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => navigate(`/members/${member.id}`)}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary-700 dark:text-primary-400">{member.name[0]}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{member.name}</div>
                        <div className="text-xs text-gray-400">{isToday ? '오늘' : formatDate(att.date)} · {att.time}</div>
                      </div>
                    </div>
                    {isToday && <span className="badge-green">오늘</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>}
      </div>

      {/* PT 잔여 3회 이하 경보 */}
      {!dataLoading && lowRemainPt.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              PT 잔여 3회 이하
            </h2>
            <button onClick={() => navigate('/pt')} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">전체보기</button>
          </div>
          <div className="space-y-1">
            {lowRemainPt.map(pt => {
              const member = members.find(m => m.id === pt.memberId);
              const remaining = pt.totalSessions - pt.usedSessions;
              if (!member) return null;
              return (
                <div key={pt.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => navigate(`/members/${member.id}`)}>
                  <div>
                    <span className="font-medium text-sm text-gray-900 dark:text-white">{member.name}</span>
                    <span className="text-xs text-gray-400 ml-2">{pt.productName}</span>
                    {pt.trainerName && <span className="text-xs text-gray-400 ml-1">· {pt.trainerName}</span>}
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${remaining === 0 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`}>
                    잔여 {remaining}회
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PT 진행 현황 */}
      {activePt.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Dumbbell className="w-4 h-4 text-purple-500" />
              PT 진행 현황
            </h2>
            <button onClick={() => navigate('/pt')} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">전체보기</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {activePt.slice(0, 6).map(pt => {
              const member = members.find(m => m.id === pt.memberId);
              const remaining = pt.totalSessions - pt.usedSessions;
              const progress = (pt.usedSessions / pt.totalSessions) * 100;
              if (!member) return null;
              return (
                <div key={pt.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" onClick={() => navigate(`/members/${member.id}`)}>
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-sm text-gray-900 dark:text-white">{member.name}</span>
                    <span className={`text-xs font-semibold ${remaining <= 5 ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>잔여 {remaining}회</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{pt.trainerName} 트레이너</div>
                  <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{pt.usedSessions}/{pt.totalSessions}회</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
